/**
 * 缩略图面片池（ADR-0004、design §3.3 §6）：远看是星，近看是照片。
 *
 * - 每 4 帧挑一次「投影最大」的照片：用空间网格（grid.ts）只查镜头附近的星，按照片在屏幕上的高度排序。
 * - 中档：128px 纹理数组，桌面 1024 层、手机 384 层；照片高过 180 像素的再换近档 512px（桌面 64 层、手机 24 层）。
 *   层按最近最少使用回收。纹理不带 mip（见 thumbs.ts 的 LEVELS），缩小显示时着色器多采几个点，照片不闪。
 * - 缩略图到之前先显示 ThumbHash 模糊图（ADR-0004）。取图、解码、缩放、生成 mip 都在 Worker 里（thumbs.worker.ts），
 *   主线程只按层上传（copyTextureToTexture），每帧限量，不卡。
 * - 面片朝向镜头，按远近排好后用预乘透明度画在叠加层上：和轮转卡片一样不参与辉光、不被色调映射压暗，但随流体扭曲。
 * - 照片随着靠近慢慢「显影」：高度 26 → 40 像素之间从星的颜色过渡到照片；快占满屏幕时淡出。
 */
import * as THREE from 'three';
import { photoPath, type PhotoDto } from '@star-sea/shared';
import type { StarGrid } from './grid.ts';
import type { StarField } from './stars.ts';
import { LEVELS, type Job, type Level, type Reply, type Work } from './thumbs.ts';

/**
 * 照片的高度是星的大小的多少倍：星的大小约 0.5–1.3，照片高约 0.25–0.6。
 * 星团里两颗星相距约 0.3，照片会叠一些，像一小摞照片；再大就把星团的光全盖住了。
 */
export const PLANE = 0.45;
const MAX_PLANE = 1.35 * PLANE;
/** 照片高度（CSS 像素）：从这里开始显影，到 FULL 完全显影 */
const SHOW = 26;
const FULL = 40;
/** 照片高过这个（CSS 像素）就换 512 的；不带 mip，512 的图缩得太小会闪，所以门槛高一些 */
const NEAR_PX = 180;
/** 迟滞：已经有的，低于门槛的 80% 才放掉，免得在门槛附近来回加载 */
const KEEP = 0.8;
/** 每几帧挑一次 */
const EVERY = 4;
/** 每帧最多上传几张（中档每张约 64 KB，近档约 1 MB） */
const BUDGET_MID = 8;
const BUDGET_NEAR = 1;
/** 同时最多几个网络请求、几个本地生成（ThumbHash、渐变） */
const INFLIGHT_NET = 6;
const INFLIGHT_LOCAL = 24;

const QUAD = new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]);
/** 请求号的特殊值：图已经到了，在等上传 */
const READY = -1;

const VERT = /* glsl */ `
attribute vec3 aPos;
// 照片的宽、高（世界单位）
attribute vec2 aSize;
// 中档层、近档层（-1 是没有）、中档出现的时刻、近档出现的时刻
attribute vec4 aLayer;
// 图在中档层、近档层里占的比例（宽、高）
attribute vec4 aUv;
attribute vec3 aColor;
uniform float uTime;
uniform float uViewH;
uniform float uOpacity;
varying vec2 vUv;
varying vec2 vSize;
varying vec4 vUvMax;
varying vec2 vLayer;
varying float vFade;
varying float vNear;
varying float vDev;
varying vec3 vColor;
void main() {
  vec4 mv = modelViewMatrix * vec4(aPos, 1.0);
  float dist = -mv.z;
  float px = dist > 0.01 ? aSize.y * uViewH * 0.5 * projectionMatrix[1][1] / dist : 0.0;
  // 显影：靠近时出现；快占满屏幕时淡出，免得一张照片糊在镜头上
  float fade = smoothstep(${SHOW.toFixed(1)}, ${FULL.toFixed(1)}, px) * (1.0 - smoothstep(uViewH * 0.55, uViewH * 0.85, px));
  fade *= clamp((uTime - aLayer.z) / 0.5, 0.0, 1.0) * uOpacity;
  if (fade <= 0.001) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  mv.xy += position.xy * aSize;
  gl_Position = projectionMatrix * mv;
  vUv = position.xy + 0.5;
  vSize = aSize;
  vUvMax = aUv;
  vLayer = aLayer.xy;
  vNear = aLayer.y >= 0.0 ? clamp((uTime - aLayer.w) / 0.35, 0.0, 1.0) : 0.0;
  vFade = fade;
  vDev = smoothstep(${SHOW.toFixed(1)}, ${(FULL * 1.25).toFixed(1)}, px);
  vColor = aColor;
}
`;

const FRAG = /* glsl */ `
uniform sampler2DArray uMid;
uniform sampler2DArray uNear;
varying vec2 vUv;
varying vec2 vSize;
varying vec4 vUvMax;
varying vec2 vLayer;
varying float vFade;
varying float vNear;
varying float vDev;
varying vec3 vColor;
float sdRound(vec2 p, vec2 b, float r) { vec2 q = abs(p) - b + r; return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r; }
// 纹理没有 mip：缩小显示（一个像素盖住好几个纹素）时按旋转网格多采 4 个点，免得照片闪
vec3 tap(sampler2DArray s, vec2 uv, vec2 dx, vec2 dy, float m, vec2 lo, vec2 hi, float layer) {
  if (m < 1.4) return texture(s, vec3(clamp(uv, lo, hi), layer)).rgb;
  vec3 c = texture(s, vec3(clamp(uv - dx * 0.125 - dy * 0.375, lo, hi), layer)).rgb;
  c += texture(s, vec3(clamp(uv + dx * 0.375 - dy * 0.125, lo, hi), layer)).rgb;
  c += texture(s, vec3(clamp(uv + dx * 0.125 + dy * 0.375, lo, hi), layer)).rgb;
  c += texture(s, vec3(clamp(uv - dx * 0.375 + dy * 0.125, lo, hi), layer)).rgb;
  return c * 0.25;
}
void main() {
  float s = min(vSize.x, vSize.y);
  vec2 p = (vUv - 0.5) * vSize;
  float d = sdRound(p, vSize * 0.5, s * 0.06);
  float aa = max(fwidth(d), 1e-5);
  float inside = 1.0 - smoothstep(-aa, aa, d);
  if (inside <= 0.0) discard;
  // 图的第一行在纹理的 t = 0；边上让出一个纹素，免得采到这一层上一张照片的残留
  vec2 st = vec2(vUv.x, 1.0 - vUv.y), ua = st * vUvMax.xy, ub = st * vUvMax.zw;
  vec2 dxa = dFdx(ua), dya = dFdy(ua), dxb = dFdx(ub), dyb = dFdy(ub);
  float ma = max(length(dxa), length(dya)) * 128.0, mb = max(length(dxb), length(dyb)) * 512.0;
  vec3 a = tap(uMid, ua, dxa, dya, ma, vec2(1.0 / 128.0), vUvMax.xy - 1.0 / 128.0, vLayer.x);
  vec3 b = vNear > 0.0 ? tap(uNear, ub, dxb, dyb, mb, vec2(1.0 / 512.0), max(vUvMax.zw - 1.0 / 512.0, vec2(1.0 / 512.0)), vLayer.y) : a;
  vec3 col = mix(a, b, vNear);
  col *= 1.0 - 0.18 * smoothstep(0.3, 0.78, length(vUv - 0.5));
  // 显影：先是星的颜色，再慢慢显出照片
  col = mix(vColor * 0.35, col, vDev);
  // 很细的一圈边，带一点星的颜色
  float rim = 1.0 - smoothstep(0.0, aa * 1.5 + s * 0.02, abs(d + s * 0.012));
  col = mix(col, mix(vColor, vec3(1.0), 0.35), rim * 0.35);
  float alpha = inside * vFade;
  gl_FragColor = vec4(min(col, vec3(1.0)) * alpha, alpha);
}
`;

interface Tier {
  readonly size: number;
  readonly layers: number;
  tex: THREE.DataArrayTexture | null;
  readonly free: number[];
  /** 每层现在属于哪颗星（-1 是空的） */
  readonly owner: Int32Array;
}

interface Entry {
  mid: number;
  near: number;
  /** 中档的图：0 还没有，1 占位图，2 清晰图（或者不会更清晰了）；近档：0 还没有，2 有了 */
  midLevel: 0 | 1 | 2;
  nearLevel: 0 | 2;
  midBorn: number;
  nearBorn: number;
  midUv: [number, number];
  nearUv: [number, number];
  /** 进行中的请求号，0 是没有，READY 是图到了、排着等上传（这期间不再要）；hashTried、netFailed 让失败的不再重试 */
  midReq: number;
  nearReq: number;
  hashTried: boolean;
  netFailed: boolean;
  nearFailed: boolean;
  /** 上次被挑中是第几轮；nearUsed 是上次够大、要近档的那一轮（近档按它回收） */
  used: number;
  nearUsed: number;
}

interface Pending {
  star: number;
  tier: 'mid' | 'near';
  /** 占位图、网络图 */
  what: 'local' | 'net';
}

export class PhotoPool {
  readonly mesh: THREE.Mesh<THREE.InstancedBufferGeometry, THREE.ShaderMaterial>;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly grid: StarGrid;
  private readonly fake: boolean;
  private readonly mid: Tier;
  private readonly near: Tier;
  private readonly entries = new Map<number, Entry>();
  private readonly jobs = new Map<number, Pending>();
  private readonly readyMid: { star: number; what: 'local' | 'net'; levels: Level[] }[] = [];
  private readonly readyNear: { star: number; levels: Level[] }[] = [];
  private worker: Worker | null = null;
  private supported = false;
  private nextJob = 1;
  private net = 0;
  private local = 0;
  private round = 0;
  private frame = 0;
  private count = 0;
  private time = 0;
  private warmed = false;
  /** 预热时用一个看不见的实例真画一次，让 Safari 把着色器管线提前编好 */
  private prime = false;
  private readonly uniforms = {
    uMid: { value: null as THREE.DataArrayTexture | null },
    uNear: { value: null as THREE.DataArrayTexture | null },
    uTime: { value: 0 },
    uViewH: { value: 800 },
    uOpacity: { value: 1 },
  };
  private readonly attrs: Record<'aPos' | 'aSize' | 'aLayer' | 'aUv' | 'aColor', THREE.InstancedBufferAttribute>;
  private readonly vp = new THREE.Matrix4();
  private readonly at = new THREE.Vector3();
  private readonly cands: number[] = [];
  private readonly pick: { i: number; px: number; w: number }[] = [];
  /** 复用的挑选结果对象，免得每轮都分配一堆小对象 */
  private readonly pickPool: { i: number; px: number; w: number }[] = [];
  /** 这一轮可以收回的层，按上次被挑中的先后排好（用到时才排一次） */
  private readonly evictable = new Map<Tier, number[]>();

  constructor(renderer: THREE.WebGLRenderer, grid: StarGrid, opts: { low: boolean; fake: boolean; token: string | null }) {
    this.renderer = renderer;
    this.grid = grid;
    this.fake = opts.fake;
    // 纹理数组的层数不能超过显卡的上限（WebGL2 至少 256）
    const gl = renderer.getContext() as WebGL2RenderingContext, maxLayers = gl.getParameter(gl.MAX_ARRAY_TEXTURE_LAYERS) as number;
    const tier = (size: number, layers: number): Tier => {
      const n = Math.min(layers, maxLayers);
      return { size, layers: n, tex: null, free: Array.from({ length: n }, (_, k) => n - 1 - k), owner: new Int32Array(n).fill(-1) };
    };
    this.mid = tier(128, opts.low ? 384 : 1024);
    this.near = tier(512, opts.low ? 24 : 64);
    const cap = this.mid.layers, geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(QUAD, 3));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    const attr = (k: number) => {
      const a = new THREE.InstancedBufferAttribute(new Float32Array(cap * k), k);
      a.setUsage(THREE.DynamicDrawUsage);
      return a;
    };
    this.attrs = { aPos: attr(3), aSize: attr(2), aLayer: attr(4), aUv: attr(4), aColor: attr(3) };
    for (const [name, a] of Object.entries(this.attrs)) geo.setAttribute(name, a);
    geo.instanceCount = 0;
    this.mesh = new THREE.Mesh(
      geo,
      new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, uniforms: this.uniforms, transparent: true, premultipliedAlpha: true, depthTest: false, depthWrite: false }),
    );
    this.mesh.frustumCulled = false;
    // 比轮转卡片先画
    this.mesh.renderOrder = -1;
    this.mesh.visible = false;
    try {
      this.worker = new Worker(new URL('./thumbs.worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = (e: MessageEvent<Reply>) => this.onReply(e.data);
      this.send({ kind: 'token', token: opts.token });
    } catch {
      // 开不了 Worker：只显示星
      this.worker = null;
    }
  }

  /** 叠加层上有没有照片 */
  get visible(): boolean {
    return this.mesh.visible;
  }

  /** 统计用：现在画了几张、各档占了几层 */
  get usage(): { shown: number; mid: number; near: number } {
    return { shown: this.count, mid: this.mid.layers - this.mid.free.length, near: this.near.layers - this.near.free.length };
  }

  /** 最多同时显影多少张（画质降档时减少，quality.ts） */
  private cap = Number.POSITIVE_INFINITY;

  setCap(n: number): void {
    this.cap = Math.max(0, n);
  }

  /** 重新连上服务后：之前因为断网没取到的图，下一轮挑选时再要一次 */
  retryFailed(): void {
    for (const e of this.entries.values()) {
      e.netFailed = false;
      e.nearFailed = false;
    }
  }

  /** 整体重新载入时：所有槽位作废 */
  reset(): void {
    for (const id of this.jobs.keys()) this.send({ kind: 'cancel', id });
    this.jobs.clear();
    this.readyMid.length = 0;
    this.readyNear.length = 0;
    this.net = this.local = 0;
    for (const t of [this.mid, this.near]) {
      t.owner.fill(-1);
      t.free.length = 0;
      for (let k = t.layers - 1; k >= 0; k--) t.free.push(k);
    }
    this.entries.clear();
    this.count = 0;
    this.mesh.geometry.instanceCount = 0;
    this.mesh.visible = false;
  }

  private send(job: Job): void {
    this.worker?.postMessage(job);
  }

  /**
   * 预热，挑看不出卡顿的时候做（入场爆炸的白光里），免得第一次靠近星团时卡一下：
   * - 两块纹理数组分配好、各写一个像素：Safari 把真正的显存分配推迟到第一次写入，实测第一次写要 50 / 35 毫秒；
   * - 下一帧用一个看不见的实例真画一次：Safari 第一次画时才编着色器管线，实测要几百毫秒。
   */
  warm(): void {
    if (this.warmed) return;
    this.warmed = true;
    const dot = [{ w: 1, h: 1, data: new ArrayBuffer(4) }];
    this.upload(this.mid, 0, dot);
    this.upload(this.near, 0, dot);
    this.prime = true;
  }

  /** opacity 为 0（入场、轮转时）就不挑也不画 */
  update(o: { time: number; camera: THREE.PerspectiveCamera; viewH: number; stars: StarField; photos: PhotoDto[]; opacity: number }): void {
    this.time = o.time;
    this.frame++;
    const u = this.uniforms;
    u.uTime.value = o.time;
    u.uViewH.value = o.viewH;
    u.uOpacity.value = o.opacity;
    this.pump();
    if (this.prime) {
      this.prime = false;
      // 已经在画照片了，管线早就编好了，不用预热
      if (this.count === 0) {
        // 一个高度为 0 的实例：顶点着色器把它放到画面外，不出像素，但整条管线会编好
        this.attrs.aSize.setXY(0, 0, 0);
        this.attrs.aSize.needsUpdate = true;
        this.mesh.geometry.instanceCount = 1;
        this.mesh.visible = true;
        return;
      }
    }
    if (o.opacity <= 0 || !this.supported) {
      this.mesh.visible = false;
      return;
    }
    if (this.frame % EVERY === 0) this.select(o);
    this.mesh.visible = this.count > 0;
  }

  private canShow(p: PhotoDto): boolean {
    return this.fake || p.thumbs || !!p.thumbhash;
  }

  private entry(i: number): Entry {
    let e = this.entries.get(i);
    if (!e)
      this.entries.set(
        i,
        (e = { mid: -1, near: -1, midLevel: 0, nearLevel: 0, midBorn: 0, nearBorn: 0, midUv: [1, 1], nearUv: [1, 1], midReq: 0, nearReq: 0, hashTried: false, netFailed: false, nearFailed: false, used: 0, nearUsed: 0 }),
      );
    return e;
  }

  /** 第一次用到时才分配显存：只分配、不上传（source.dataReady = false），之后按层写 */
  private alloc(t: Tier): THREE.DataArrayTexture {
    if (t.tex) return t.tex;
    const tex = new THREE.DataArrayTexture(null, t.size, t.size, t.layers);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.source.dataReady = false;
    tex.needsUpdate = true;
    this.renderer.initTexture(tex);
    t.tex = tex;
    if (t === this.mid) this.uniforms.uMid.value = tex;
    else this.uniforms.uNear.value = tex;
    return tex;
  }

  /**
   * 拿一层：先用空的，没有就收回最久没用到的那张（这一轮用到的不收）。
   * 近档按「上次够大」算：还在画面里、但已经缩小到只用中档的照片，近档层可以让给新的。
   */
  private take(t: Tier, star: number): number {
    let layer = t.free.pop();
    if (layer === undefined) {
      let list = this.evictable.get(t);
      if (!list) {
        // 每轮最多排一次序；层占满后每张新照片都线性扫一遍会很慢
        const near = t === this.near;
        const used = (k: number) => {
          const e = this.entries.get(t.owner[k]!);
          return e ? (near ? e.nearUsed : e.used) : -1;
        };
        list = [];
        for (let k = 0; k < t.layers; k++) if (used(k) < this.round) list.push(k);
        list.sort((a, b) => used(b) - used(a));
        this.evictable.set(t, list);
      }
      while (layer === undefined && list.length) {
        const k = list.pop()!, owner = t.owner[k]!, e = this.entries.get(owner);
        if (!e || (t === this.near ? e.nearUsed : e.used) >= this.round) continue;
        this.release(t, owner);
        layer = t.free.pop();
      }
      if (layer === undefined) return -1;
    }
    t.owner[layer] = star;
    return layer;
  }

  private release(t: Tier, star: number): void {
    const e = this.entries.get(star);
    if (!e) return;
    if (t === this.mid) {
      // 中档没了，近档也一起放掉（画的时候要先有中档）
      if (e.near >= 0) this.release(this.near, star);
      if (e.mid >= 0) {
        this.mid.owner[e.mid] = -1;
        this.mid.free.push(e.mid);
      }
      if (e.midReq) this.cancel(e.midReq);
      this.entries.delete(star);
    } else {
      if (e.near >= 0) {
        this.near.owner[e.near] = -1;
        this.near.free.push(e.near);
      }
      if (e.nearReq) this.cancel(e.nearReq);
      e.near = -1;
      e.nearLevel = 0;
      e.nearReq = 0;
    }
  }

  private cancel(id: number): void {
    const job = this.jobs.get(id);
    if (!job) return;
    this.jobs.delete(id);
    if (job.what === 'net') this.net--;
    else this.local--;
    this.send({ kind: 'cancel', id });
  }

  private request(star: number, tier: 'mid' | 'near', what: 'local' | 'net', job: Work): number {
    const id = this.nextJob++;
    this.jobs.set(id, { star, tier, what });
    if (what === 'net') this.net++;
    else this.local++;
    this.send({ ...job, id });
    return id;
  }

  private onReply(r: Reply): void {
    if (r.kind === 'ready') {
      this.supported = r.supported;
      return;
    }
    const job = this.jobs.get(r.id);
    if (!job) return;
    this.jobs.delete(r.id);
    if (job.what === 'net') this.net--;
    else this.local--;
    const e = this.entries.get(job.star);
    if (!e) return;
    if (job.tier === 'mid') {
      if (!r.ok) {
        e.midReq = 0;
        if (job.what === 'net') e.netFailed = true;
        return;
      }
      e.midReq = READY;
      this.readyMid.push({ star: job.star, what: job.what, levels: r.levels });
    } else {
      if (!r.ok) {
        e.nearReq = 0;
        e.nearFailed = true;
        this.release(this.near, job.star);
        return;
      }
      e.nearReq = READY;
      this.readyNear.push({ star: job.star, levels: r.levels });
    }
  }

  private upload(t: Tier, layer: number, levels: Level[]): [number, number] {
    const tex = this.alloc(t);
    this.at.set(0, 0, layer);
    for (let l = 0; l < LEVELS && l < levels.length; l++) {
      const lv = levels[l]!;
      this.renderer.copyTextureToTexture(new THREE.DataTexture(new Uint8Array(lv.data), lv.w, lv.h), tex, null, this.at, 0, l);
    }
    return [levels[0]!.w / t.size, levels[0]!.h / t.size];
  }

  /** 把 Worker 送回来的图按层上传，每帧限量（已经作废的不算在限量里） */
  private pump(): void {
    for (let n = 0; n < BUDGET_MID && this.readyMid.length; ) {
      const r = this.readyMid.shift()!, e = this.entries.get(r.star);
      if (!e || e.mid < 0) continue;
      if (e.midReq === READY) e.midReq = 0;
      // 清晰图已经到了，晚到的占位图不要盖上去
      if (r.what === 'local' && e.midLevel === 2) continue;
      e.midUv = this.upload(this.mid, e.mid, r.levels);
      if (e.midLevel === 0) e.midBorn = this.time;
      // 压测模式的渐变就是最终的图
      e.midLevel = r.what === 'net' || this.fake ? 2 : 1;
      n++;
    }
    for (let n = 0; n < BUDGET_NEAR && this.readyNear.length; ) {
      const r = this.readyNear.shift()!, e = this.entries.get(r.star);
      if (!e || e.near < 0) continue;
      if (e.nearReq === READY) e.nearReq = 0;
      e.nearUv = this.upload(this.near, e.near, r.levels);
      e.nearLevel = 2;
      e.nearBorn = this.time;
      n++;
    }
  }

  /** 挑照片、要图、写实例属性 */
  private select(o: { camera: THREE.PerspectiveCamera; viewH: number; stars: StarField; photos: PhotoDto[] }): void {
    this.round++;
    this.evictable.clear();
    const cam = o.camera;
    cam.updateMatrixWorld();
    const e = this.vp.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse).elements;
    const pxScale = o.viewH * 0.5 * cam.projectionMatrix.elements[5]!;
    const pos = o.stars.positions, size = o.stars.sizes, n = o.stars.count;
    // 最大的照片也小于门槛的距离之外不用看
    const reach = (MAX_PLANE * pxScale) / (SHOW * KEEP);
    const cands = this.grid.near(cam.position.x, cam.position.y, cam.position.z, reach, this.cands);
    const pick = this.pick;
    pick.length = 0;
    for (const i of cands) {
      if (i >= n) continue;
      const p = o.photos[i];
      if (!p || !this.canShow(p)) continue;
      const x = pos[i * 3]!, y = pos[i * 3 + 1]!, z = pos[i * 3 + 2]!;
      const w = e[3]! * x + e[7]! * y + e[11]! * z + e[15]!;
      if (w <= 0.1) continue;
      const cx = (e[0]! * x + e[4]! * y + e[8]! * z + e[12]!) / w, cy = (e[1]! * x + e[5]! * y + e[9]! * z + e[13]!) / w;
      if (Math.abs(cx) > 1.15 || Math.abs(cy) > 1.15) continue;
      const px = (size[i]! * PLANE * pxScale) / w, had = this.entries.get(i);
      if (px < SHOW * (had && had.midLevel > 0 ? KEEP : 1)) continue;
      const q = this.pickPool[pick.length] ?? (this.pickPool[pick.length] = { i: 0, px: 0, w: 0 });
      q.i = i;
      q.px = px;
      q.w = w;
      pick.push(q);
    }
    pick.sort((a, b) => b.px - a.px);
    if (pick.length > Math.min(this.mid.layers, this.cap)) pick.length = Math.min(this.mid.layers, this.cap);
    // 这一轮挑中的先标上，分配层时不会被收回；够大、要近档的另外标上（最多近档的层数那么多张）
    let nearCount = 0;
    for (const q of pick) {
      const en = this.entry(q.i), p = o.photos[q.i]!;
      en.used = this.round;
      if (nearCount < this.near.layers && !en.nearFailed && (this.fake || p.thumbs) && q.px >= NEAR_PX * (en.nearLevel === 2 ? KEEP : 1)) {
        en.nearUsed = this.round;
        nearCount++;
      }
    }
    for (const q of pick) {
      const en = this.entry(q.i), p = o.photos[q.i]!;
      if (en.mid < 0) {
        en.mid = this.take(this.mid, q.i);
        if (en.mid < 0) continue;
        this.alloc(this.mid);
      }
      const aspect = Math.min(1.8, Math.max(0.56, p.width && p.height ? p.width / p.height : 4 / 3));
      // 先要占位图（ThumbHash 或压测用的渐变），再要 128 的缩略图
      if (en.midLevel === 0 && !en.midReq && !en.hashTried && this.local < INFLIGHT_LOCAL) {
        en.hashTried = true;
        if (this.fake) en.midReq = this.request(q.i, 'mid', 'local', { kind: 'swatch', color: p.color, aspect, size: this.mid.size });
        else if (p.thumbhash) en.midReq = this.request(q.i, 'mid', 'local', { kind: 'hash', hash: p.thumbhash, size: this.mid.size });
      }
      if (en.midLevel < 2 && !en.midReq && !en.netFailed && !this.fake && p.thumbs && this.net < INFLIGHT_NET) {
        en.midReq = this.request(q.i, 'mid', 'net', { kind: 'photo', url: photoPath(p.id, 't128'), size: this.mid.size });
      }
      // 近档：照片够大才要 512 的
      if (en.nearUsed === this.round) {
        if (en.near < 0) {
          en.near = this.take(this.near, q.i);
          if (en.near >= 0) this.alloc(this.near);
        }
        // 压测模式的近档图是本地画的渐变，和别的本地任务一起限量
        if (en.near >= 0 && en.nearLevel === 0 && !en.nearReq && (this.fake ? this.local < INFLIGHT_LOCAL : this.net < INFLIGHT_NET + 2)) {
          en.nearReq = this.fake
            ? this.request(q.i, 'near', 'local', { kind: 'swatch', color: p.color, aspect, size: this.near.size })
            : this.request(q.i, 'near', 'net', { kind: 'photo', url: photoPath(p.id, 't512'), size: this.near.size });
        }
      }
    }
    this.write(pick, o);
  }

  /** 有图的才画；远的先画，近的盖在上面 */
  private write(pick: { i: number; px: number; w: number }[], o: { stars: StarField; photos: PhotoDto[] }): void {
    const show = pick.filter((q) => (this.entries.get(q.i)?.midLevel ?? 0) > 0).sort((a, b) => b.w - a.w);
    const { aPos, aSize, aLayer, aUv, aColor } = this.attrs;
    const P = aPos.array as Float32Array, S = aSize.array as Float32Array, L = aLayer.array as Float32Array, U = aUv.array as Float32Array, C = aColor.array as Float32Array;
    const pos = o.stars.positions, size = o.stars.sizes, col = o.stars.colors;
    let m = 0;
    for (const q of show) {
      const en = this.entries.get(q.i)!, p = o.photos[q.i]!;
      const aspect = Math.min(1.8, Math.max(0.56, p.width && p.height ? p.width / p.height : 4 / 3)), h = size[q.i]! * PLANE;
      P.set([pos[q.i * 3]!, pos[q.i * 3 + 1]!, pos[q.i * 3 + 2]!], m * 3);
      S.set([h * aspect, h], m * 2);
      const near = en.near >= 0 && en.nearLevel === 2;
      L.set([en.mid, near ? en.near : -1, en.midBorn, en.nearBorn], m * 4);
      U.set([en.midUv[0], en.midUv[1], en.nearUv[0], en.nearUv[1]], m * 4);
      C.set([col[q.i * 3]!, col[q.i * 3 + 1]!, col[q.i * 3 + 2]!], m * 3);
      m++;
    }
    for (const a of Object.values(this.attrs)) {
      a.clearUpdateRanges();
      a.addUpdateRange(0, Math.max(1, m) * a.itemSize);
      a.needsUpdate = true;
    }
    this.count = m;
    this.mesh.geometry.instanceCount = m;
  }
}
